<template>
  <div>
    <HeroHeader
      v-if="!embedded"
      eyebrow="PROPIEDAD"
      :title="'Impuestos — ' + (estate.description || '…')"
      :back="true"
      @back="$router.go(-1)"
      :compact="true"
    >
      <template #toolbar>
        <button class="btn btn--outline" @click="showGenerateSidebar = true">Generar impuestos</button>
        <button class="btn" @click="showNewSidebar = true">+ Agregar</button>
      </template>
    </HeroHeader>

    <!-- Embebido: el mes de la página y las CTAs de esta pestaña comparten fila,
         igual que en Gastos. Suelto: el mes lo maneja esta vista y la fila la
         completa con los chips de estado. -->
    <div v-if="period" class="filter-row">
      <MonthNav :model-value="period" @update:model-value="$emit('update:period', $event)"/>
      <div class="filter-row__actions">
        <button class="btn btn--outline btn--sm" @click="showGenerateSidebar = true">Generar impuestos</button>
        <button class="btn btn--sm" @click="showNewSidebar = true">+ Agregar</button>
      </div>
    </div>
    <template v-else>
      <div v-if="embedded" class="embedded-toolbar">
        <button class="btn btn--outline btn--sm" @click="showGenerateSidebar = true">Generar impuestos</button>
        <button class="btn btn--sm" @click="showNewSidebar = true">+ Agregar</button>
      </div>
      <div class="filter-row">
        <MonthNav v-model="localPeriod"/>
        <ChipsBar :chips="statusChips" v-model="filterStatus"/>
      </div>
    </template>

    <ChipsBar v-if="period" :chips="statusChips" v-model="filterStatus"/>

    <div class="filter-bar filter-bar--no-top">
      <SearchInput v-model="search" placeholder="Buscar por tipo…" label="Buscar impuesto por tipo"/>
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div class="dt-wrapper">
      <table class="dt" v-if="filteredList.length > 0">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Total</th>
            <th>Vencimiento</th>
            <th>Estado</th>
            <th aria-label="Acciones"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in displayList" :key="o.id">
            <td>{{ o.type }}</td>
            <td><span class="dt-cell__primary">{{ formatAmount(o.total, o.currency) }}</span></td>
            <td>{{ formatDate(o.deadline) }}</td>
            <td>
              <span class="badge" :class="o.paid ? 'badge--success' : 'badge--error'">
                {{ o.paid ? 'Pagado' : 'Pendiente' }}
              </span>
            </td>
            <td>
              <div class="dt-actions">
                <span v-if="!o.paid" class="dt-icon" title="Editar"
                      @click="showUpdateSidebar = true; sidebarData = o">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </span>
                <span class="dt-icon" title="Registrar pago"
                      @click="showPaySidebar = true; sidebarData = o">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <EmptyState
        v-else
        title="Sin impuestos"
        :message="emptyMessage"
      />
    </div>

    <NewTax :estate="estate" @close="showNewSidebar = false" @add="addSidebar"
            :class="{'show': showNewSidebar}" title="Nuevo impuesto" :show-navbar="showNewSidebar"/>
    <UpdateTax :data="sidebarData" @close="showUpdateSidebar = false" @update="updateSidebar"
               :class="{'show': showUpdateSidebar}" title="Editar impuesto" :show-navbar="showUpdateSidebar"/>
    <GenerateTax :estate="estate" @close="showGenerateSidebar = false" @add="addSidebar"
                 :class="{'show': showGenerateSidebar}" title="Generar impuestos" :show-navbar="showGenerateSidebar"/>
    <Pay :data="sidebarData" @close="showPaySidebar = false" @update="updateSidebar"
         :class="{'show': showPaySidebar}" :show-navbar="showPaySidebar"/>
  </div>
</template>

<script>
import { db } from '@/firebase/firebaseConfig'
import NewTax from './NewTax'
import UpdateTax from './UpdateTax'
import GenerateTax from './Generate'
import Pay from './Pay'
import HeroHeader from '@/components/HeroHeader'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import MonthNav from '@/components/MonthNav'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import { currentPeriod, formatPeriod, inPeriod } from '@/utils/period'
import moment from 'moment'

export default {
  name: 'PropertyTaxes',
  components: { NewTax, UpdateTax, GenerateTax, Pay, HeroHeader, ChipsBar, EmptyState, SortSelect, MonthNav, SearchInput },
  props: {
    // Modo embebido dentro del detalle de propiedad: oculta el hero propio
    embedded: { type: Boolean, default: false },
    // Id de la propiedad a mostrar; si no viene, se toma de $route.params.id
    estateId: { type: String, default: null },
    // Período 'YYYY-MM' impuesto por la página contenedora. Si viene, manda él y
    // esta vista no dibuja su propio selector de mes.
    period: { type: String, default: null }
  },
  emits: ['update:period'],
  data () {
    return {
      list: [],
      estate: { currency: {} },
      // Mes propio, sólo para el modo suelto (embebido manda la prop period)
      localPeriod: currentPeriod(),
      search: '',
      filterStatus: 'all',
      sortBy: 'deadline_asc',
      sortOptions: [
        { value: 'deadline_asc', label: 'Vencimiento (próximo primero)', field: 'deadline', type: 'date', dir: 'asc' },
        { value: 'deadline_desc', label: 'Vencimiento (lejano primero)', field: 'deadline', type: 'date', dir: 'desc' },
        { value: 'amount_desc', label: 'Monto (mayor a menor)', field: 'total', type: 'number', dir: 'desc' },
        { value: 'type', label: 'Tipo (A–Z)', field: 'type', type: 'string', dir: 'asc' },
        { value: 'status', label: 'Estado (pendientes primero)', field: 'paid', type: 'bool', dir: 'asc' }
      ],
      showNewSidebar: false,
      showUpdateSidebar: false,
      showGenerateSidebar: false,
      showPaySidebar: false,
      sidebarData: {}
    }
  },
  computed: {
    // El mes vigente: el que impone la página si está embebido, si no el propio
    activePeriod () { return this.period || this.localPeriod },
    statusChips () {
      return [{ label: 'Todos', value: 'all' }, { label: 'Pendientes', value: 'pending' }, { label: 'Pagados', value: 'paid' }]
    },
    // Impuestos cuyo vencimiento cae en el mes seleccionado
    monthList () {
      return this.list.filter(o => inPeriod(o.deadline, this.activePeriod))
    },
    filteredList () {
      let list = this.monthList
      // Filtros secundarios DENTRO del mes seleccionado
      if (this.filterStatus === 'pending') list = list.filter(o => !o.paid)
      else if (this.filterStatus === 'paid') list = list.filter(o => o.paid)
      if (this.search.trim()) {
        const q = this.search.trim().toLowerCase()
        list = list.filter(o => (o.type || '').toLowerCase().includes(q))
      }
      return list
    },
    displayList () {
      return sortItems(this.filteredList, this.sortOptions.find(o => o.value === this.sortBy))
    },
    emptyMessage () {
      if (this.monthList.length === 0) return `No hay impuestos en ${formatPeriod(this.activePeriod)}.`
      return 'No hay impuestos que coincidan con los filtros.'
    }
  },
  async created () {
    try {
      this.$store.dispatch('showProgress')
      await this.getEstate()
      await this.getTaxes()
    } catch (e) {
      this.$toast.error('Error al cargar datos')
    } finally {
      this.$store.dispatch('hideProgress')
    }
  },
  methods: {
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    formatAmount (amount, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount || 0)
    },
    addSidebar (obj) { this.list.push(obj) },
    updateSidebar (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
    },
    async getEstate () {
      const id = this.estateId || this.$route.params.id
      const doc = await db.collection('estates').doc(id).get()
      if (doc.exists) this.estate = { id: doc.id, ...doc.data() }
    },
    async getTaxes () {
      const snap = await db.collection('estates').doc(this.estate.id)
        .collection('propertyTaxes').orderBy('deadline', 'asc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.deadline = doc.data().deadline.toDate()
        obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
        this.list.push(obj)
      })
    }
  }
}
</script>

<style scoped>
/* CTAs propios cuando el módulo va embebido en el detalle de una propiedad */
.embedded-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 24px 0;
}
</style>
